import { Box, VStack, Text, HStack, Badge, Skeleton, Tooltip } from "@chakra-ui/react";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { useFutarchyProposals } from "../../hooks/useFutarchyProposals";
import { FutarchyProposal } from "../../rest/kairos/types";
import { ThemeColor } from "../../theme/palette";
import { Link } from "../Link/Link";

const getStatusColor = (status: FutarchyProposal["status"]) => {
    switch (status) {
        case "active":
            return ThemeColor.KAIROS;
        case "passed":
            return "green";
        case "rejected":
            return "red";
        case "executed":
            return "blue";
        default:
            return "gray";
    }
};

const ProposalCard = ({ proposal }: { proposal: FutarchyProposal }) => {
    return (
        <Box
            p={6}
            borderRadius="8px"
            backgroundColor="whiteAlpha.100"
            border="0.5px solid"
            borderColor="rgba(95, 244, 244, 0.2)"
            transition="all 0.2s"
            _hover={{
                backgroundColor: "whiteAlpha.200",
                borderColor: ThemeColor.KAIROS,
            }}
        >
            <VStack spacing={4} align="stretch">
                <HStack justify="space-between" align="flex-start">
                    <VStack align="flex-start" spacing={2} flex="1">
                        <Text fontSize="lg" fontWeight="bold" color="white">
                            {proposal.title}
                        </Text>
                        <Text fontSize="sm" color="whiteAlpha.700">
                            {proposal.description}
                        </Text>
                    </VStack>
                    <Badge
                        colorScheme={getStatusColor(proposal.status)}
                        px={3}
                        py={1}
                        borderRadius="full"
                        textTransform="capitalize"
                    >
                        {proposal.status}
                    </Badge>
                </HStack>

                {(proposal.yesMarketPrice !== undefined || proposal.noMarketPrice !== undefined) && (
                    <HStack spacing={4} mt={2}>
                        {proposal.yesMarketPrice !== undefined && (
                            <Box>
                                <Text fontSize="xs" color="whiteAlpha.600">
                                    YES Market
                                </Text>
                                <Text fontSize="md" fontWeight="bold" color="green.400">
                                    ${proposal.yesMarketPrice.toFixed(2)}
                                </Text>
                            </Box>
                        )}
                        {proposal.noMarketPrice !== undefined && (
                            <Box>
                                <Text fontSize="xs" color="whiteAlpha.600">
                                    NO Market
                                </Text>
                                <Text fontSize="md" fontWeight="bold" color="red.400">
                                    ${proposal.noMarketPrice.toFixed(2)}
                                </Text>
                            </Box>
                        )}
                    </HStack>
                )}

                <HStack justify="space-between" fontSize="xs" color="whiteAlpha.600">
                    <Text>Proposed by: {proposal.proposer.slice(0, 4)}...{proposal.proposer.slice(-4)}</Text>
                    {proposal.endDate && (
                        <Text>
                            Ends: {new Date(proposal.endDate).toLocaleDateString()}
                        </Text>
                    )}
                </HStack>
            </VStack>
        </Box>
    );
};

export const FutarchyProposals = () => {
    const { proposals, isLoading, error } = useFutarchyProposals();

    return (
        <Box
            p={6}
            borderRadius="8px"
            backgroundColor="whiteAlpha.100"
        >
            <VStack spacing={6} align="stretch">
                <VStack spacing={4} align="stretch">
                    <Text fontSize="xl" fontWeight="bold" color="white" textAlign="center">
                        Futarchy Governance
                    </Text>

                    <Box
                        p={4}
                        borderRadius="8px"
                        backgroundColor="rgba(95, 244, 244, 0.08)"
                        border="0.5px solid"
                        borderColor="rgba(95, 244, 244, 0.2)"
                    >
                        <VStack spacing={3} align="flex-start">
                            <HStack>
                                <Text fontSize="md" fontWeight="semibold" color={ThemeColor.KAIROS}>
                                    What is Futarchy?
                                </Text>
                                <Tooltip
                                    label="Futarchy combines prediction markets with governance. Proposals are evaluated through market prices, where YES/NO markets determine outcomes based on collective wisdom."
                                    placement="top"
                                >
                                    <InfoOutlineIcon color={ThemeColor.KAIROS} />
                                </Tooltip>
                            </HStack>
                            <Text fontSize="sm" color="whiteAlpha.800" lineHeight="1.6">
                                Futarchy is a governance mechanism that uses prediction markets to make decisions. 
                                When a proposal is created, two markets are opened: one for "YES" (proposal passes) 
                                and one for "NO" (proposal fails). The market prices reflect the collective prediction 
                                of the proposal's outcome. If the YES market trades higher than the NO market, the 
                                proposal is more likely to pass. This system leverages market incentives to aggregate 
                                information and make better governance decisions.
                            </Text>
                            <Link 
                                to="https://metadao.fi/projects/kyros" 
                                textColor={ThemeColor.KAIROS}
                                target="_blank"
                                rel="noopener noreferrer"
                                fontSize="sm"
                                fontWeight="semibold"
                                _hover={{ textDecoration: "underline" }}
                            >
                                Learn more on MetaDAO →
                            </Link>
                        </VStack>
                    </Box>
                </VStack>

                {isLoading ? (
                    <VStack spacing={4} align="stretch">
                        <Skeleton height="150px" borderRadius="8px" />
                        <Skeleton height="150px" borderRadius="8px" />
                    </VStack>
                ) : error ? (
                    <Box p={4} borderRadius="8px" backgroundColor="whiteAlpha.200" color="white">
                        <Text textAlign="center">{error}</Text>
                    </Box>
                ) : proposals.length === 0 ? (
                    <Box
                        p={6}
                        borderRadius="8px"
                        backgroundColor="whiteAlpha.100"
                        border="0.5px solid"
                        borderColor="rgba(95, 244, 244, 0.2)"
                        transition="all 0.2s"
                        _hover={{
                            backgroundColor: "whiteAlpha.200",
                            borderColor: ThemeColor.KAIROS,
                        }}
                        as="a"
                        href="/governance/kyros-1"
                        cursor="pointer"
                    >
                        <VStack spacing={4} align="stretch">
                            <HStack justify="space-between" align="flex-start">
                                <VStack align="flex-start" spacing={2} flex="1">
                                    <Text fontSize="lg" fontWeight="bold" color="white">
                                        KYROS-1: Burn 4.38M unclaimed KYROS airdrop allocation
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.700">
                                        Burn 4,379,383 unclaimed KYROS from the initial airdrop allocation.
                                    </Text>
                                </VStack>
                                <Badge
                                    colorScheme="green"
                                    px={3}
                                    py={1}
                                    borderRadius="full"
                                    textTransform="capitalize"
                                >
                                    Approved
                                </Badge>
                            </HStack>

                            <HStack justify="space-between" fontSize="xs" color="whiteAlpha.600">
                                <Text>View full proposal →</Text>
                            </HStack>
                        </VStack>
                    </Box>
                ) : (
                    <VStack spacing={4} align="stretch">
                        {proposals.map((proposal) => (
                            <ProposalCard key={proposal.id} proposal={proposal} />
                        ))}
                    </VStack>
                )}
            </VStack>
        </Box>
    );
};

